import type { WritableComputedRef } from 'vue';

// Résolveur de version par feature pour le Design System v2 (Claude Design).
//
// Source de vérité : runtimeConfig.public.designVersions (bakée au build via
// --build-arg, cf. nuxt.config.ts + docs/design-system-v1-integration-report.md §12).
// Chaque feature majeure progresse indépendamment : la valeur EST la version
// ('legacy' | 'v2' | … extensible), pas un booléen → un futur 'v3' s'ajoute sans
// changer ce mécanisme.
//
// Override sans rebuild (les versions sont bakées au build : sans lui, revenir en arrière imposerait
// de repasser une CI) :
//   • query : ?fv=chrome:legacy — mémorisé pour le navigateur par plugins/ds-feature-override.client
//   • localStorage : clé 'ds.fv' au même format ; `?fv=` vide efface la mémorisation.
// Les DEUX SENS passent, sur tous les environnements, production comprise : le paramètre ouvre une
// feature autant qu'il la referme.
// une bascule globale reste un rebuild avec un autre NUXT_PUBLIC_FEATURE_*_VERSION.
//
// Rappel : flag public = masquage UI, PAS une sécurité. L'autorisation reste côté API.

// NB : `mobile` n'a pas de rendu legacy en face — c'est un flag d'ouverture au public (`legacy` =
// shell fermé, l'utilisateur reste sur l'app desktop même sous 1024px), à la forme des autres pour
// rester couvert par le kill-switch. Les valeurs par défaut sont dans `nuxt.config.ts` ; ce qui est
// réellement servi en production est tenu par docs/reference/registre-de-bascule.md.
export const DESIGN_FEATURES = [
  'chrome',
  'mobile',
  'simulation',
  'analyse',
  'documents',
  'messages',
  'edition',
  'recap',
] as const;

export type DesignFeature = (typeof DESIGN_FEATURES)[number];
export type FeatureVersion = 'legacy' | 'v2';

// Les écrans de contenu v2 sont stylés par le kit `.gd-*`, mais le fond, la typo et la couleur
// de texte viennent du shell (`.gd-app` / `.gd-main`, cf. assets/styles/ds/kit.css), monté par
// layouts/default.vue au flag `chrome`. Rendus dans le chrome legacy, ils s'affichent donc sur
// fond blanc. Une feature de contenu en v2 IMPLIQUE le chrome v2 ; l'inverse est faux — le
// chrome v2 peut habiller des écrans encore legacy, c'est le sens de la migration.
// 'mobile' est exclu : le shell mobile (MobileApp) se substitue au chrome, il ne l'exige pas.
const FEATURES_REQUIRING_CHROME_V2: readonly DesignFeature[] = [
  'simulation',
  'analyse',
  'documents',
  'messages',
  'edition',
  'recap',
];

// Ni la variable d'environnement ni `?fv=` n'agissent sur ces features-là, cf.
// docs/reference/registre-de-bascule.md.
const ALWAYS_OPEN_FEATURES: readonly DesignFeature[] = ['edition', 'recap'];

const pullsChromeToV2 = (feature: DesignFeature): boolean =>
  FEATURES_REQUIRING_CHROME_V2.includes(feature) && !ALWAYS_OPEN_FEATURES.includes(feature);

export const FEATURE_OVERRIDE_STORAGE_KEY = 'ds.fv';
export const FEATURE_OVERRIDE_QUERY_KEY = 'fv';

function readOverrideStorage (): string | null {
  try {
    return window.localStorage.getItem(FEATURE_OVERRIDE_STORAGE_KEY);
  } catch {
    // localStorage indisponible (mode privé, etc.).
    return null;
  }
}

// Référence unique sur l'override mémorisé, lue par le résolveur et écrite par le plugin : le
// storage n'est pas réactif, un `getItem()` posé dans un computed n'y serait relu qu'au changement
// d'une AUTRE dépendance. Scope détaché, durée de vie de l'app.
let overrideStorage: WritableComputedRef<string | null> | null = null;

export function useFeatureOverrideStorage (): WritableComputedRef<string | null> {
  overrideStorage ??= effectScope(true).run(() => {
    const state = ref<string | null>(import.meta.client ? readOverrideStorage() : null);
    return computed<string | null>({
      get: () => state.value,
      set: (value) => {
        state.value = value;
        try {
          if (value === null) {
            window.localStorage.removeItem(FEATURE_OVERRIDE_STORAGE_KEY);
          } else {
            window.localStorage.setItem(FEATURE_OVERRIDE_STORAGE_KEY, value);
          }
        } catch {
          // localStorage indisponible : l'override reste valable pour la navigation en cours.
        }
      },
    });
  }) as WritableComputedRef<string | null>;
  return overrideStorage;
}

const isKnownFeature = (value: string): value is DesignFeature =>
  (DESIGN_FEATURES as readonly string[]).includes(value);

const isKnownVersion = (value: string): value is FeatureVersion =>
  value === 'legacy' || value === 'v2';

function parseOverrides (raw: string | null | undefined): Partial<Record<DesignFeature, FeatureVersion>> {
  const out: Partial<Record<DesignFeature, FeatureVersion>> = {};
  if (!raw) { return out; }
  for (const pair of raw.split(',')) {
    const [feature, version] = pair.split(':').map(part => part.trim());
    if (feature && version && isKnownFeature(feature) && isKnownVersion(version)) {
      out[feature] = version;
    }
  }
  return out;
}

export function useFeatureVersion (): {
  versionOf: (feature: DesignFeature) => FeatureVersion;
  isV2: (feature: DesignFeature) => boolean;
  DESIGN_FEATURES: typeof DESIGN_FEATURES;
} {
  const config = useRuntimeConfig();
  const route = useRoute();

  const forceLegacy = config.public.designForceLegacy === true;
  const baseMap = (config.public.designVersions ?? {}) as Record<DesignFeature, FeatureVersion>;

  const stored = import.meta.client ? useFeatureOverrideStorage() : null;

  // Réactifs sur la query ET sur le stockage : poser `?fv=…` bascule sans rechargement.
  const overrides = computed<Partial<Record<DesignFeature, FeatureVersion>>>(() => {
    const rawQuery = route.query[FEATURE_OVERRIDE_QUERY_KEY];
    const fromQuery = parseOverrides(typeof rawQuery === 'string' ? rawQuery : undefined);
    return { ...parseOverrides(stored?.value), ...fromQuery };
  });

  // Version déclarée pour la feature, sans les dépendances inter-features. `baseMap` vient de
  // variables d'env build-time : une valeur hors ('legacy'|'v2') y retombe sur legacy — même
  // fail-safe qu'un flag absent (les overrides dev sont déjà validés par parseOverrides).
  const declaredVersionOf = (feature: DesignFeature): FeatureVersion => {
    if (ALWAYS_OPEN_FEATURES.includes(feature)) { return 'v2'; }
    const declared = overrides.value[feature] ?? baseMap[feature] ?? 'legacy';
    return isKnownVersion(declared) ? declared : 'legacy';
  };

  // Résolue à part : c'est la version du chrome qui arbitre la cohérence de l'ensemble.
  const chromeVersion = (): FeatureVersion => {
    // Un override explicite prime sur la dépendance dérivée : c'est le seul moyen de retomber sur
    // le chrome legacy quand une feature de contenu est déjà ouverte.
    const forced = overrides.value.chrome;
    if (forced !== undefined) { return forced; }
    if (FEATURES_REQUIRING_CHROME_V2.some(dependent =>
      pullsChromeToV2(dependent) && declaredVersionOf(dependent) === 'v2')) {
      return 'v2';
    }
    return declaredVersionOf('chrome');
  };

  const versionOf = (feature: DesignFeature): FeatureVersion => {
    if (forceLegacy) { return 'legacy'; }
    if (feature === 'chrome') { return chromeVersion(); }
    const declared = declaredVersionOf(feature);
    // La dépendance vaut dans les deux sens : replier le chrome replie le contenu qui l'exige,
    // sinon l'override ouvre l'état mixte (écran v2 hors du shell) qu'elle sert à empêcher.
    if (declared === 'v2'
      && FEATURES_REQUIRING_CHROME_V2.includes(feature)
      && chromeVersion() === 'legacy') {
      return 'legacy';
    }
    return declared;
  };

  const isV2 = (feature: DesignFeature): boolean => versionOf(feature) === 'v2';

  return { versionOf, isV2, DESIGN_FEATURES };
}
